import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { unstable_useBlocker as useBlocker } from "react-router-dom";
import { MiModalConfirm } from "@miplatform/components";
import { BUTTON_LABELS, MODAL_TITLES } from "@miplatform/constants";

const UnsavedChangesContext = createContext(null);

const defaultShouldBlock = ({ currentLocation, nextLocation }) =>
  currentLocation.pathname !== nextLocation.pathname;

export function UnsavedChangesProvider({ children }) {
  const guardRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const registerGuard = useCallback((options) => {
    guardRef.current = options;
  }, []);

  const unregisterGuard = useCallback(() => {
    guardRef.current = null;
  }, []);

  const blocker = useBlocker(
    useCallback((args) => {
      const guard = guardRef.current;
      if (!guard?.isDirty) return false;
      return (guard.shouldBlock ?? defaultShouldBlock)(args);
    }, [])
  );

  const handleCancel = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const handleDiscard = useCallback(() => {
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  const handleSaveAndProceed = useCallback(async () => {
    if (blocker.state !== "blocked" || !guardRef.current) return;
    setIsSaving(true);
    const success = await guardRef.current.onSave().catch(() => false);
    setIsSaving(false);
    if (success) blocker.proceed();
  }, [blocker]);

  const value = useMemo(
    () => ({ registerGuard, unregisterGuard }),
    [registerGuard, unregisterGuard]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <MiModalConfirm
        open={blocker.state === "blocked"}
        loading={isSaving}
        title={MODAL_TITLES.UNSAVED_CHANGES}
        description="If you leave this page, your changes will be lost."
        textNegative={BUTTON_LABELS.CANCEL}
        textSecondaryPositive={BUTTON_LABELS.DISCARD_CHANGES}
        textPositive={BUTTON_LABELS.SAVE_CHANGES}
        handleNegative={handleCancel}
        handleSecondaryPositive={handleDiscard}
        handlePositive={handleSaveAndProceed}
      />
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChangesContext must be used within an UnsavedChangesProvider"
    );
  }
  return context;
}

/**
 * @param {{ isDirty: boolean, onSave: () => Promise<boolean>, shouldBlock?: (args: { currentLocation, nextLocation, historyAction }) => boolean }} options
 */
export function useUnsavedChangesGuard({ isDirty, onSave, shouldBlock }) {
  const { registerGuard, unregisterGuard } = useUnsavedChangesContext();

  useEffect(() => {
    registerGuard({ isDirty, onSave, shouldBlock });
    return () => unregisterGuard();
  }, [registerGuard, unregisterGuard, isDirty, onSave, shouldBlock]);
}
